<template>
  <div class="modal-card" style="width: auto">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <header class="modal-card-head">
      <p class="modal-card-title has-text-centered">
        {{ $tc("settings.newCashback") }}
      </p>
    </header>

    <section class="modal-card-body">
      <div class="columns">
        <div class="column is-half is-offset-one-quarter">
          <b-field
            class="mb-5"
            :label="$t('settings.cashbackType')"
            label-position="on-border"
            :type="cashbackTypeFormType"
            :message="
              cashbackTypeFormType === 'is-danger'
                ? $t('common.requiredField')
                : ''
            "
          >
            <b-select
              v-model="cashback.type"
              :placeholder="$t('settings.selectCashbackType')"
              expanded
              @blur="cashbackTypeHasBeenTouched = true"
            >
              <option value="" disabled>
                {{ $t("settings.selectCashbackType") }}
              </option>

              <option value="general">
                {{ $t("settings.cashbackTypeGeneral") }}
              </option>

              <option value="category">
                {{ $t("settings.cashbackTypeCategory") }}
              </option>

              <option value="subcategory">
                {{ $t("settings.cashbackTypeSubcategory") }}
              </option>

              <option value="product">
                {{ $t("settings.cashbackTypeProduct") }}
              </option>

              <!-- <option value="scaleProduct">
                {{ $t("scaleProducts.cashbackTypeScaleProduct") }}
              </option> -->
            </b-select>
          </b-field>

          <div
            class="columns"
            v-show="
              cashback.type === 'category' ||
                cashback.type === 'subcategory' ||
                cashback.type === 'product'
            "
          >
            <div
              class="column"
              v-show="
                cashback.type === 'category' || cashback.type === 'subcategory'
              "
            >
              <b-field
                class="mb-5"
                :label="$t('settings.cashbackTypeCategory')"
                label-position="on-border"
                :type="cashbackCategoryFormType"
                :message="
                  cashbackCategoryFormType === 'is-danger'
                    ? $t('common.requiredField')
                    : ''
                "
              >
                <b-select
                  v-model="category"
                  id="category"
                  :placeholder="$t('settings.cashbackTypeCategory')"
                  expanded
                  @input="fillSubcategories"
                  @blur="cashbackCategoryHasBeenTouched = true"
                ></b-select>
              </b-field>
            </div>

            <div
              class="column"
              v-show="cashback.type === 'subcategory' && category !== ''"
            >
              <b-field
                :label="$t('settings.cashbackTypeSubcategory')"
                label-position="on-border"
                :type="cashbackSubCategoryFormType"
                :message="
                  cashbackSubCategoryFormType === 'is-danger'
                    ? $t('common.requiredField')
                    : ''
                "
              >
                <b-select
                  v-model="subcategory"
                  id="subcategory"
                  :placeholder="$t('settings.cashbackTypeSubcategory')"
                  expanded
                  @blur="cashbackSubCategoryHasBeenTouched = true"
                ></b-select>
              </b-field>
            </div>

            <div class="column is-narrow" v-show="cashback.type === 'product'">
              <b-field>
                <b-input
                  v-model="barcode"
                  :placeholder="$t('products.code')"
                  type="tel"
                  icon="barcode"
                  @input.native="formatOnlyDigits('barcode')"
                  @keydown.native="productName = ''"
                  @keyup.enter.native="searchProduct"
                ></b-input>

                <p class="control">
                  <button
                    class="button"
                    :style="{
                      backgroundColor: $primaryColor,
                      color: $primaryColorContrast
                    }"
                    @click="searchProduct"
                  >
                    {{ $t("settings.search") }}
                  </button>
                </p>
              </b-field>
            </div>
          </div>

          <div
            class="mb-6"
            v-show="cashback.type === 'product' && productName !== ''"
          >
            <p :class="!isMobile ? 'product-name' : ''">
              <b-icon icon="check" type="is-success"></b-icon>
              {{ productName }}
            </p>
          </div>

          <b-field
            class="mb-5"
            :label="$t('settings.cashbackPercentage')"
            label-position="on-border"
            :type="cashbackValueFormType"
            :message="
              cashbackValueFormType === 'is-danger'
                ? $t('coupon.couponRequiredMinValue')
                : ''
            "
          >
            <b-input
              :placeholder="$t('settings.cashbackPercentage')"
              type="number"
              min="1"
              max="100"
              v-model="cashback.percentage"
              onkeypress="return event.charCode >= 48 && event.charCode <= 57"
              @blur="cashbackValueHasBeenTouched = true"
            ></b-input>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('settings.cashbackBeginDate')"
            label-position="on-border"
          >
            <b-input
              :placeholder="$t('settings.cashbackSelectBeginDate')"
              icon="calendar-day"
              custom-class="is-clickable"
              @click.native="openBeginDateModal"
              @focus="openBeginDateModal"
              id="beginDateId"
              :value="getBeginDateFormattedValue"
            ></b-input>
          </b-field>

          <b-modal
            :active.sync="isBeginDateModalActive"
            can-cancel
            :on-cancel="saveBeginDateAfterModalClose"
          >
            <b-datetimepicker
              v-model="cashback.beginDate"
              inline
              class="has-text-centered"
              locale="pt-BR"
            >
              <template #right>
                <b-button
                  :label="$t('common.ok')"
                  type="is-primary"
                  @click="
                    saveBeginDateAfterModalClose;
                    isBeginDateModalActive = false;
                  "
                />
              </template>
            </b-datetimepicker>
          </b-modal>

          <b-field
            class="mb-5"
            :label="$t('settings.cashbackEndDate')"
            label-position="on-border"
          >
            <b-input
              :placeholder="$t('settings.cashbackSelectEndDate')"
              icon="calendar-day"
              custom-class="is-clickable"
              @click.native="openEndDateModal"
              @focus="openEndDateModal"
              id="endDateId"
              :value="getEndDateFormattedValue"
            ></b-input>
          </b-field>

          <b-modal
            :active.sync="isEndDateModalActive"
            can-cancel
            :on-cancel="saveEndDateAfterModalClose"
          >
            <b-datetimepicker
              v-model="cashback.endDate"
              inline
              class="has-text-centered"
              locale="pt-BR"
            >
              <template #right>
                <b-button
                  :label="$t('common.ok')"
                  type="is-primary"
                  @click="
                    saveEndDateAfterModalClose;
                    isEndDateModalActive = false;
                  "
                />
              </template>
            </b-datetimepicker>
          </b-modal>

          <b-field
            :label="$t('common.status')"
            label-position="on-border"
            class="mb-5"
          >
            <div class="block" style="position: relative; top: 10px;">
              <b-radio
                v-model="cashback.isActive"
                name="cashback.isActive"
                :native-value="true"
              >
                {{ $t("common.Active") }}
              </b-radio>

              <b-radio
                v-model="cashback.isActive"
                name="cashback.isActive"
                :native-value="false"
              >
                {{ $t("common.Inactive") }}
              </b-radio>
            </div>
          </b-field>
        </div>
      </div>
    </section>

    <footer class="modal-card-foot" style="justify-content: space-around;">
      <div class="columns">
        <div class="column">
          <button class="button" type="button" @click="$parent.close()">
            {{ $t("common.cancel") }}
          </button>

          <button
            class="button"
            :style="{
              backgroundColor: $primaryColor,
              color: $primaryColorContrast
            }"
            type="button"
            @click="createCashback"
            :disabled="!isAbleToRegister"
          >
            {{ $t("settings.createCashback") }}
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../../mixins/common";
import { mask } from "../../../mixins/mask";
import { responsive } from "../../../mixins/responsive";

export default {
  mixins: [common, mask, responsive],

  data() {
    return {
      isBeginDateModalActive: false,
      isEndDateModalActive: false,
      isLoading: false,
      cashbackTypeHasBeenTouched: false,
      cashbackCategoryHasBeenTouched: false,
      cashbackSubCategoryHasBeenTouched: false,
      cashbackValueHasBeenTouched: false,
      beginDateFormHasBeenTouched: false,
      endDateFormHasBeenTouched: false,
      cashback: {
        type: "",
        percentage: "",
        beginDate: null,
        endDate: null,
        isActive: true
      },
      category: "",
      subcategory: "",
      barcode: "",
      productName: ""
    };
  },

  methods: {
    createCashback() {
      if (this.cashback.percentage < 1 || this.cashback.percentage > 100) {
        this.alertError({ message: "INVALID_CASHBACK_PERCENTAGE" });
        return;
      } else if (this.cashback.beginDate > this.cashback.endDate) {
        this.alertError({
          message: "CASHBACK_BEGIN_DATE_GREATER_THAN_END_DATE"
        });
        return;
      }
      this.isLoading = true;
      this.$store
        .dispatch("createCashback", {
          storeId: this.storeBeingManaged._id,
          cashback: this.cashback,
          category: this.category,
          subcategory: this.subcategory,
          barcode: this.barcode
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$emit("increment-key");
            this.$buefy.toast.open({
              duration: 3000,
              message: this.$t("settings.cashbackCreated"),
              position: "is-bottom",
              type: "is-primary"
            });
            this.$parent.close();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getCategories() {
      this.isLoading = true;
      this.$store
        .dispatch("getCategories")
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.fillCategories();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    fillCategories() {
      let i,
        len = this.categories.length;
      let categoryOptions = `<option value="" disabled>${this.$t(
        "settings.pickCategory"
      )}</option>`;
      for (i = 0; i < len; i++) {
        categoryOptions += `<option value="${this.categories[i]._id}">${this.categories[i].name}</option>`;
      }
      document.getElementById("category").innerHTML = categoryOptions;
    },

    fillSubcategories() {
      if (this.category !== "") {
        let subcategoryOptions = `<option value="" disabled>${this.$t(
          "settings.pickSubcategory"
        )}</option>`;

        let category = this.categories.filter(c => {
          return c._id === this.category;
        })[0];

        if (category) {
          let subcategories = category.subcategories;
          const len = subcategories.length;

          for (let i = 0; i < len; i++) {
            subcategoryOptions += `<option value="${subcategories[i]._id}">${subcategories[i].name}</option>`;
          }
        }
        document.getElementById("subcategory").innerHTML = subcategoryOptions;
      }
    },

    searchProduct() {
      if (!/\d+|^CB+\d+$/.test(this.barcode)) {
        this.alertError({ message: "INVALID_BARCODE" });
        return;
      }
      this.isLoading = true;
      this.$store
        .dispatch("searchProduct", { barcode: this.barcode })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.productName = res.data.payload.product.n;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    openBeginDateModal() {
      this.isBeginDateModalActive = true;
    },

    openEndDateModal() {
      this.isEndDateModalActive = true;
    },

    saveBeginDateAfterModalClose() {
      if (this.cashback.beginDate !== null) {
        let beginDateEl = document.getElementById("beginDateId");
        beginDateEl.setAttribute("value", this.cashback.beginDate);
      }
    },

    saveEndDateAfterModalClose() {
      if (this.cashback.endDate !== null) {
        let endDateEl = document.getElementById("endDateId");
        endDateEl.setAttribute("value", this.cashback.endDate);
      }
    }
  },

  computed: {
    ...mapGetters(["storeBeingManaged", "categories", "preferences"]),

    getBeginDateFormattedValue() {
      return this.cashback.beginDate !== null
        ? this.moment
            .utc(this.cashback.beginDate)
            .add(this.preferences.timezone, "h")
            .format("DD/MM/YY, HH:mm")
        : "";
    },

    getEndDateFormattedValue() {
      return this.cashback.endDate !== null
        ? this.moment
            .utc(this.cashback.endDate)
            .add(this.preferences.timezone, "h")
            .format("DD/MM/YY, HH:mm")
        : "";
    },

    cashbackTypeFormType() {
      return this.cashbackTypeHasBeenTouched && this.cashback.type === ""
        ? "is-danger"
        : "";
    },

    cashbackCategoryFormType() {
      return this.cashbackCategoryHasBeenTouched && this.category === ""
        ? "is-danger"
        : "";
    },

    cashbackSubCategoryFormType() {
      return this.cashbackSubCategoryHasBeenTouched && this.subcategory === ""
        ? "is-danger"
        : "";
    },

    cashbackValueFormType() {
      return this.cashbackValueHasBeenTouched && this.cashback.percentage === ""
        ? "is-danger"
        : "";
    },

    beginDateFormType() {
      return this.beginDateFormHasBeenTouched &&
        this.cashback.beginDate === null
        ? "is-danger"
        : "";
    },

    endDateFormType() {
      return this.endDateFormHasBeenTouched && this.cashback.endDate === null
        ? "is-danger"
        : "";
    },

    isAbleToRegister() {
      return (
        this.cashback.percentage > 0 &&
        this.cashback.percentage < 101 &&
        this.cashback.beginDate &&
        this.cashback.endDate &&
        (this.cashback.type === "general" ||
          (this.cashback.type === "category" && this.category !== "") ||
          (this.cashback.type === "subcategory" &&
            this.category !== "" &&
            this.subcategory !== "") ||
          (this.cashback.type === "product" &&
            this.barcode !== "" &&
            this.productName !== ""))
      );
    }
  },

  watch: {
    "cashback.type": function(value) {
      if (
        !this.categories.length &&
        (value === "category" || value === "subcategory")
      ) {
        this.getCategories();
        this.fillSubcategories();
      } else if (this.categories.length) {
        this.fillCategories();
        this.fillSubcategories();
      }
    },
    "cashback.percentage"(v) {
      if (v > 100) {
        this.cashback.percentage = 100;
      } else if (v < 0) {
        this.cashback.percentage = 0;
      }
    }
  }
};
</script>

<style scoped>
.product-name {
  position: relative;
  top: 6px;
}
</style>
